/**
 *  Server-Sent Events (SSE) API
 * @param {*} url 服务地址
 * @param {*} options 配置项
 * @returns
 */

const SSE = function (url, options) {
  // 如果没有传入url参数，则抛出错误
  if (!(this instanceof SSE)) {
    return new SSE(url, options);
  }

  this.sessionId = options.sessionId || '';

  this.logger = window.logger || console;

  this.INITIALIZING = -1;
  this.CONNECTING = 0;
  this.OPEN = 1;
  this.CLOSED = 2;
  this.PENDING = 3;
  this.DONE = 4;

  this.url = url;

  options = options || {};
  this.headers = options.headers || {};
  this.payload = options.payload !== undefined ? options.payload : '';
  this.method = options.method || (this.payload && 'POST') || 'GET';
  this.withCredentials = !!options.withCredentials;

  this.FIELD_SEPARATOR = ':';
  this.listeners = {};

  this.xhr = null;
  this.readyState = this.INITIALIZING;
  this.progress = 0;
  this.chunk = '';

  /** 流不活跃检测定时器 ID（网络断开后服务端停发心跳帧，用此兜底） */
  this._inactivityTimer = null;

  this.addEventListener = function (type, listener) {
    if (this.listeners[type] === undefined) {
      this.listeners[type] = [];
    }

    if (this.listeners[type].indexOf(listener) === -1) {
      this.listeners[type].push(listener);
    }
  };

  this.removeEventListener = function (type, listener) {
    if (this.listeners[type] === undefined) {
      return;
    }

    const filtered = [];
    this.listeners[type].forEach(function (element) {
      if (element !== listener) {
        filtered.push(element);
      }
    });
    if (filtered.length === 0) {
      delete this.listeners[type];
    } else {
      this.listeners[type] = filtered;
    }
  };

  this.dispatchEvent = function (e) {
    if (!e) {
      return true;
    }

    e.source = this;

    const onHandler = 'on' + e.type;
    if (this.hasOwnProperty(onHandler)) {
      this[onHandler].call(this, e);
      if (e.defaultPrevented) {
        return false;
      }
    }

    if (this.listeners[e.type]) {
      return this.listeners[e.type].every(function (callback) {
        callback(e);
        return !e.defaultPrevented;
      });
    }

    return true;
  };

  this._setReadyState = function (state) {
    const event = new CustomEvent(`${this.sessionId}_readystatechange`);
    event.readyState = state;
    this.readyState = state;
    this.dispatchEvent(event);
  };

  this._onStreamFailure = function (e) {
    const event = new CustomEvent(`${this.sessionId}_error`);
    event.data = e.currentTarget.response;
    // XHR error 事件（非 HTTP 错误）触发时 response 为空字符串，标记为网络层错误
    event.isNetworkError = e.currentTarget.response === '';
    this.dispatchEvent(event);
    this.close();
  };

  this._onStreamAbort = function () {
    this.dispatchEvent(new CustomEvent(`${this.sessionId}_abort`));
    this.close();
  };

  this._onStreamProgress = function (e) {
    if (!this.xhr) {
      return;
    }

    // 每次收到进度事件（包括服务端心跳帧）时重置不活跃定时器
    this._resetInactivityTimer();

    if (this.xhr.status !== 200) {
      this._onStreamFailure(e);
      return;
    }

    if (this.readyState == this.CONNECTING) {
      this.dispatchEvent(new CustomEvent(`${this.sessionId}_open`));
      this._setReadyState(this.OPEN);
    }

    const data = this.xhr.responseText.substring(this.progress);
    this.progress += data.length;
    data.split(/(\r\n|\r|\n){2}/g).forEach(
      function (part) {
        if (part.trim().length === 0) {
          this.dispatchEvent(this._parseEventChunk(this.chunk.trim()));
          this.chunk = '';
        } else {
          this.chunk += part;
        }
      }.bind(this)
    );
  };

  this._onStreamLoaded = function (e) {
    this._onStreamProgress(e);

    // Parse the last chunk.
    this.dispatchEvent(this._parseEventChunk(this.chunk));
    this.chunk = '';
  };

  /**
   * 将接收到的SSE事件块解析为构造的事件对象。
   */
  this._parseEventChunk = function (chunk) {
    if (!chunk || chunk.length === 0) {
      return null;
    }

    const e = { id: null, retry: null, data: '', event: `${this.sessionId}_message` };
    // // 接口报错 异常等情况
    // if (!chunk.startsWith('data:')) {
    //   this.logger.error({
    //     msg: 'SSE chunk does not start with "data:"',
    //     chunk: chunk,
    //     type: 'error'
    //   });
    //   const event = new CustomEvent(`${this.sessionId}_error`);
    //   event.data = chunk;
    //   return event;
    // }
    let hasDataField = false;

    chunk.split(/\n|\r\n|\r/).forEach(
      function (line) {
        line = line.trimRight();
        // SSE 注释/心跳帧以 ":" 开头，按规范应忽略，不能视为错误。
        if (!line || line.startsWith(':')) {
          return;
        }
        const index = line.indexOf(this.FIELD_SEPARATOR);
        if (index < 0) {
          // 不符合 "field:value" 结构的行直接忽略，避免误报中断正常流。
          return;
        }

        const field = line.substring(0, index);
        if (!(field in e)) {
          return;
        }

        const value = line.substring(index + 1).trimLeft();
        if (field === 'data') {
          hasDataField = true;
          e[field] += value;
        } else {
          e[field] = value;
        }
      }.bind(this)
    );

    if (!hasDataField) {
      return null;
    }

    const event = new CustomEvent(e.event);
    event.data = e.data;
    event.id = e.id;
    return event;
  };

  this._checkStreamClosed = function () {
    if (!this.xhr) return;
    if (this.xhr.readyState === this.DONE) {
      this._setReadyState(this.CLOSED);
      this.dispatchEvent(new CustomEvent(`${this.sessionId}_done`));
    }
  };

  /**
   * 重置流不活跃检测定时器。
   * 每次收到 XHR 进度事件（含服务端心跳帧 ": "）时调用，重置 30 秒倒计时。
   * 若 30 秒内未再收到任何数据，认为网络已断开（路由失败/服务端静默断连），
   * 主动派发网络错误事件并关闭连接，让上层及时呈现断网提示。
   */
  this._resetInactivityTimer = function () {
    clearTimeout(this._inactivityTimer);
    this._inactivityTimer = setTimeout(() => {
      if (this.readyState === this.CLOSED) return;
      const event = new CustomEvent(`${this.sessionId}_error`);
      event.data = null;
      event.isNetworkError = true;
      this.dispatchEvent(event);
      this.close();
    }, 30000);
  };

  // 出发事件
  this.stream = function () {
    this._setReadyState(this.CONNECTING);

    this.xhr = new XMLHttpRequest();
    this.xhr.addEventListener('progress', this._onStreamProgress.bind(this));
    this.xhr.addEventListener('load', this._onStreamLoaded.bind(this));
    this.xhr.addEventListener('readystatechange', this._checkStreamClosed.bind(this));
    this.xhr.addEventListener('error', this._onStreamFailure.bind(this));
    this.xhr.addEventListener('abort', this._onStreamAbort.bind(this));

    // 监听浏览器断网事件：window.offline 发生时 XHR 不会立即触发 error，
    // 需主动派发网络错误并关闭连接，让上层及时感知并给用户提示
    this._networkOfflineHandler = () => {
      if (this.readyState === this.CLOSED) return;
      const event = new CustomEvent(`${this.sessionId}_error`);
      event.data = null;
      event.isNetworkError = true;
      this.dispatchEvent(event);
      this.close();
    };
    window.addEventListener('offline', this._networkOfflineHandler);

    this.xhr.open(this.method, this.url);
    for (const header in this.headers) {
      this.xhr.setRequestHeader(header, this.headers[header]);
    }
    this.xhr.withCredentials = this.withCredentials;
    this.xhr.send(this.payload);
    // 连接建立后立即启动不活跃检测定时器
    this._resetInactivityTimer();
  };

  this.close = function () {
    if (this.readyState === this.CLOSED) {
      return;
    }

    // 清理不活跃检测定时器
    clearTimeout(this._inactivityTimer);
    this._inactivityTimer = null;

    // 清理断网监听，避免 SSE 关闭后的 offline 事件重复触发
    if (this._networkOfflineHandler) {
      window.removeEventListener('offline', this._networkOfflineHandler);
      this._networkOfflineHandler = null;
    }

    this.xhr.abort();
    this.xhr = null;
    this._setReadyState(this.CLOSED);
  };
};

export const post = async function (url, payload, options) {
  return await new SSE(url, {
    method: 'POST',
    payload: JSON.stringify(payload),
    ...options
  });
};

export default SSE;
